import { useId } from "react";
import { getAccessibleTextColor, getSecondaryTextColor } from "utils/helpers";

export default function Input({
  id,
  label = "",
  labelInBorder,
  bgColor = "FFFFFF",
  disabled = false,
  invalidRingColor,
  ...props
}) {
  const invalid = props["aria-invalid"] === true || props["aria-invalid"] === "true";
  // The better-contrast of black or white, so the label and typed text clear
  // WCAG AA on any background colour, mid tones included.
  const contrastColor = getAccessibleTextColor(bgColor);
  // The visible label is the input's accessible name only through a real
  // htmlFor/id pair (WCAG 1.3.1 / 4.1.2); a caller's `id` wins, otherwise
  // useId() keeps the pair unique per instance.
  const reactId = useId();
  const inputId = id || reactId;

  return (
    <div className="relative">
      <label
        htmlFor={inputId}
        // In the border, the label's glyphs start 16px in (10px offset + 6px
        // padding), the same as the typed text (px-4), so the two line up.
        className={`font-inter inline-block px-1.5 font-medium ${
          labelInBorder
            ? "uppercase tracking-wider text-[10px] -top-1.5 left-2.5 absolute "
            : ""
        }`}
        style={{ background: bgColor, color: contrastColor }}
      >
        {label}
      </label>
      <div>
        <input
          id={inputId}
          className="block w-full min-h-[48px] rounded-md border-0 px-4 py-3 text-base text-gray-900 shadow-sm ring-1 ring-inset ring-[color:var(--input-ring)] placeholder:text-[color:var(--input-placeholder)] focus:ring-2 focus:ring-inset focus:ring-mb-main aria-[invalid=true]:ring-2"
          style={{
            background: bgColor,
            color: contrastColor,
            // The placeholder in the secondary tone, which clears AA 4.5:1 on any
            // background (a fixed gray was 2.2:1 on a light gallery).
            "--input-placeholder": getSecondaryTextColor(bgColor),
            // The unfocused boundary: the secondary tone, which clears 3:1
            // (WCAG 1.4.11) on a light gallery too, or, when invalid, a 2px
            // ring in the caller's error colour (also 3:1 against `bgColor`).
            // It feeds only the base ring, so focus still swaps in the magenta
            // ring and keyboard focus stays visible on an invalid field.
            "--input-ring": invalid
              ? invalidRingColor || contrastColor
              : getSecondaryTextColor(bgColor),
          }}
          disabled={disabled}
          {...props}
        />
      </div>
    </div>
  );
}
